import { Component, h, Prop } from '@stencil/core';
import state from '@ugc-store';
import { CSOrVendorComments, defaultReview, IReview, ReviewImageType } from '@ugc-components/ugc-pdp-review/interface';
import { FontType, detectIsRTL } from '@ugc-utils/utils';
import { toTranslatedText } from '@ugc-components/ugc-pdp-review/utils';

@Component({
  tag: 'ugc-review-item',
  styleUrl: 'ugc-review-item.scss',
  shadow: false,
})
export class UgcReviewItem {
  @Prop() reviewItem: IReview = defaultReview;
  @Prop() isModal?: boolean = false;
  @Prop() handleClickImage?: ({ assetId, reviewId, imageUrl }) => void;

  getReviewInfo = () => {
    const { switchLanguage } = state;
    const { id, reviewTitle, reviewText, languageCode, translatedTo } = this.reviewItem;
    const switchLanguageItem = switchLanguage[id];
    let toggledReviewTitle = reviewTitle;
    let toggledReviewText = reviewText;
    let toggledDirection = detectIsRTL(translatedTo || languageCode) ? 'rtl' : 'ltr';
    if (switchLanguageItem && switchLanguageItem.isShowOriginalLanguage) {
      if (switchLanguageItem.reviewTitle) {
        toggledReviewTitle = switchLanguageItem.reviewTitle;
      }
      if (switchLanguageItem.reviewText) {
        toggledReviewText = switchLanguageItem.reviewText;
        toggledDirection = detectIsRTL(languageCode) ? 'rtl' : 'ltr';
      }
    }
    return {
      reviewTitle: toggledReviewTitle,
      reviewText: toggledReviewText,
      direction: toggledDirection,
    };
  };

  render() {
    const { reviewStaticLanguage: { IDS_MSG_SHOW_MORE = '', IDS_MSG_SHOW_LESS = '' } = {}, isDesktop } = state;
    const {
      id,
      profileImageUrl,
      customerProfileLink,
      customerNickname,
      ratingValue,
      postedDateLocalized,
      badge,
      summary: { reviewCount, imageCount, answerCount, helpfulCount },
      images,
      translatedTo,
      languageCode,
      translatedToLanguageName,
      languageName,
      verifiedPurchase,
      hasRewarded,
      hasThumbsUp,
      helpfulYes,
      helpfulNo,
      shareUrl,
      customerServiceComments,
      vendorComments,
      country,
    } = this.reviewItem;

    const isDesktopModal = isDesktop && this.isModal;
    const isLargeType = isDesktop && !this.isModal;
    const fontType = isLargeType ? FontType.large : FontType.small;
    const translatedText = toTranslatedText({ translatedTo, languageName });
    const { reviewTitle, reviewText, direction } = this.getReviewInfo();

    return (
      <div>
        <div class={`${isDesktopModal && 'modal-review-item-content'}`}>
          <div class="mb-16 flex-row-start-center gap-8">
            <ugc-review-avatar size="48" profileImageUrl={profileImageUrl} customerProfileLink={customerProfileLink} />
            <div class="flex-row-between-start flex-wrap grow-1">
              <div class="flex-col-start-start">
                <div class="flex-row-center-center gap-8">
                  <ugc-review-nickname
                    fontType={fontType}
                    customerProfileLink={customerProfileLink}
                    customerNickname={customerNickname}
                  />

                  <ugc-review-badge badge={badge} reviewId={id} />
                </div>
                {!!country && <span class={`mt-4 ${isDesktop ? 'fs-14 lh-20' : 'fs-12 lh-26'}`}>{country}</span>}
                <div class="mt-4">
                  <ugc-review-profile-summary
                    fontType={fontType}
                    reviewCount={reviewCount}
                    imageCount={imageCount}
                    answerCount={answerCount}
                    helpfulCount={helpfulCount}
                  />
                </div>
              </div>
              <ugc-review-postdate fontType={fontType} postedDateLocalized={postedDateLocalized} />
            </div>
          </div>
          <div
            class={`${isLargeType ? 'mb-8 flex-row-start-center flex-wrap gap-8' : 'mb-4 flex-col-start-start gap-4'}`}
          >
            <ugc-with-link url={shareUrl}>
              <ugc-star withTips={false} disabled={true} size={isLargeType ? 24 : 20} realValue={ratingValue} />
            </ugc-with-link>
            <span dir={direction}>
              <ugc-review-title reviewTitle={reviewTitle} shareUrl={shareUrl} fontType={fontType} />
            </span>
          </div>
          <ugc-review-labels fontType={fontType} verifiedPurchase={verifiedPurchase} hasRewarded={hasRewarded} />
          <div class="my-16" dir={direction}>
            <ugc-ellipsis-text
              line={isLargeType ? 3 : 6}
              text={reviewText}
              url={shareUrl}
              readMore={IDS_MSG_SHOW_MORE}
              readLess={IDS_MSG_SHOW_LESS}
              contentStyle={{ fontSize: isLargeType ? '16px' : '14px' }}
              buttonStyle={{ fontSize: isLargeType ? '14' : '12px' }}
              iconSize={isLargeType ? '24' : '15'}
              data-testid="pdp-review-text"
            />
          </div>
          <div class={`${!this.isModal && 'flex-row-start-center'}`}>
            <ugc-review-translated-from translatedText={translatedText} />
            {isDesktop && translatedTo && (
              <ugc-review-switch-language
                reviewId={id}
                translatedTo={translatedTo}
                translatedToLanguageName={translatedToLanguageName}
                languageCode={languageCode}
                languageName={languageName}
              />
            )}
          </div>

          <div class="my-16">
            <ugc-medical-disclaimer fontType={fontType} />
          </div>

          {isDesktopModal && <ugc-review-modal-images images={images} handleClickImage={this.handleClickImage} />}

          {!this.isModal && <ugc-review-content-images data={this.reviewItem} type={ReviewImageType.InReviewList} />}

          {customerServiceComments && (
            <div class="mb-16">
              <ugc-review-comments
                fontType={fontType}
                type={CSOrVendorComments.CS}
                date={customerServiceComments.postedDateLocalized}
                content={customerServiceComments.content}
              />
            </div>
          )}
          {vendorComments && (
            <div class="mb-16">
              <ugc-review-comments
                fontType={fontType}
                type={CSOrVendorComments.Brand}
                date={vendorComments.postedDateLocalized}
                content={vendorComments.content}
              />
            </div>
          )}
        </div>
        <div class={`flex-row-between-center ${isDesktopModal && 'modal-review-item-opration'}`}>
          <div class="flex-row-start-center">
            <ugc-review-vote
              rid={id}
              hasThumbsUp={hasThumbsUp}
              helpfulYes={helpfulYes}
              helpfulNo={helpfulNo}
              fontType={fontType}
            />
          </div>
          <ugc-review-operate reviewItem={this.reviewItem} isModal={this.isModal} />
        </div>
      </div>
    );
  }
}
